import type { DropdownListVariant, DropdownOption } from '@/shared/types/dropdown';

export interface OrderDropdownListOptionsParams {
  filteredOptions: DropdownOption[];
  multiple: boolean;
  withCheckbox: boolean;
  selectedOptions: DropdownOption[];
  idName: string;
  valueName: string;
  compareValues: (a: string | number, b: string | number) => boolean;
  listVariant: DropdownListVariant;
}

/**
 * Moves selected rows to the top of the dropdown panel when ordering is unambiguous
 * (no group-title rows in the filtered list). Skipped for grouped lists to preserve headers.
 */
export function orderDropdownListOptionsPinSelectedFirst(
  params: OrderDropdownListOptionsParams
): DropdownOption[] {
  const {
    filteredOptions,
    multiple,
    withCheckbox,
    selectedOptions,
    idName,
    valueName,
    compareValues,
    listVariant,
  } = params;

  const list = filteredOptions;
  if (list.length <= 1) return list;
  if (list.some((o) => o.isGroupTitle === true)) return list;

  if (listVariant === 'toggle' || listVariant === 'checkbox-item') {
    const on: DropdownOption[] = [];
    const off: DropdownOption[] = [];
    for (const o of list) {
      if (o.checked === true) on.push(o);
      else off.push(o);
    }
    return [...on, ...off];
  }

  if (multiple && withCheckbox) {
    if (selectedOptions.length === 0) return list;
    const seen = new Set<string | number>();
    const selectedFirst: DropdownOption[] = [];
    for (const sel of selectedOptions) {
      const key = sel[idName] as string | number;
      if (seen.has(key)) continue;
      const match = list.find(
        (o) => o.isGroupTitle !== true && compareValues(o[idName] as string | number, key)
      );
      if (match) {
        selectedFirst.push(match);
        seen.add(key);
      }
    }
    const rest: DropdownOption[] = [];
    for (const o of list) {
      if (o.isGroupTitle === true) continue;
      const key = o[idName] as string | number;
      if (!seen.has(key)) rest.push(o);
    }
    return [...selectedFirst, ...rest];
  }

  if (!multiple) {
    const sel = selectedOptions[0];
    if (!sel) return list;
    const sid = sel[idName] as string | number;
    if (sid === -1 || sid === '-1' || sel[valueName] === -1 || sel[valueName] === '-1') {
      return list;
    }
    const idx = list.findIndex(
      (o) => o.isGroupTitle !== true && compareValues(o[idName] as string | number, sid)
    );
    if (idx <= 0) return list;
    const next = [...list];
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    return next;
  }

  return list;
}
